import { useLayoutEffect, useRef, useState } from 'react';
import {
  ADMIN_EVENT_LABELS,
  type DailyPoint,
  DEFAULT_THEME,
  type EventBreakdown,
  type FeatureAdoption,
  GRANT_FOREVER,
  type QuestionResult,
  type Screen as PollScreen,
  SIGNUP_SOURCE_LABELS,
  type SignupSourceBreakdown,
  type Theme,
  type WorkspaceUsageRow,
} from '@floorpoll/shared';
import { AdoptionBars } from '../../components/AdoptionBars.js';
import { EventDayStrip } from '../../components/EventDayStrip.js';
import { Brand, Logo } from '../../components/Brand.js';
import { CodeCells } from '../../components/CodeCells.js';
import { Ledger } from '../../components/Ledger.js';
import { ResultBars } from '../../components/ResultBars.js';
import { StatTile } from '../../components/StatTile.js';
import { UsageColumns } from '../../components/UsageColumns.js';
import { VendorMark } from '../../components/VendorMark.js';
import { WorkspaceUsageTable } from '../../components/WorkspaceUsageTable.js';
import { PlanView } from '../account/PlanCard.js';
import { canMoveQuestion, moveQuestion } from '../quizzes/questionMoves.js';
import { RunningOrder } from '../quizzes/RunningOrder.js';
import { ScreenEditor } from '../quizzes/ScreenEditor.js';
import { ThemeEditor } from '../quizzes/ThemeEditor.js';

/**
 * Dev-only style gallery (mounted only when import.meta.env.DEV).
 *
 * The console requires a signed-in session and the live views require a live
 * quiz, so this page renders the same class structures with fixture data —
 * enough to see and screenshot every visual state without a backend.
 */

const QUESTIONS: QuestionResult[] = [
  {
    questionId: 'q1',
    text: 'Which release should we ship first?',
    totalVotes: 48,
    options: [
      { optionId: 'o1', text: 'Realtime collaboration', count: 27, percentage: 56.3 },
      { optionId: 'o2', text: 'Offline mode', count: 14, percentage: 29.2 },
      { optionId: 'o3', text: 'Public API', count: 7, percentage: 14.6 },
    ],
  },
];

/** An NPS question as the projector draws it (#50): 40 ratings, clustered on 8. */
const npsVotes: QuestionResult = (() => {
  const counts = [0, 0, 1, 0, 2, 3, 4, 8, 12, 6, 4];
  const total = counts.reduce((sum, count) => sum + count, 0);
  return {
    questionId: 'q3',
    text: 'How likely are you to recommend us to a friend?',
    type: 'scale',
    scale: 'npsZeroToTen',
    totalVotes: total,
    options: counts.map((count, point) => ({
      optionId: `n${point}`,
      text: String(point),
      count,
      percentage: Math.round((count / total) * 1000) / 10,
    })),
  };
})();

/** 48 voters, 81 picks between them — percentages of people, so they sum to 169. */
const multiVotes: QuestionResult = {
  questionId: 'q2',
  text: 'Which releases should we ship this quarter?',
  totalVotes: 48,
  multiSelect: true,
  options: [
    { optionId: 'o1', text: 'Realtime collaboration', count: 41, percentage: 85.4 },
    { optionId: 'o2', text: 'Offline mode', count: 22, percentage: 45.8 },
    { optionId: 'o3', text: 'Public API', count: 18, percentage: 37.5 },
  ],
};

const USAGE_WEEKS = [3, 5, 4, 8, 6, 11, 9, 14, 12, 18, 16, 23].map((count, index) => ({
  weekStart: `2026-0${index < 4 ? 5 : 6}-${String(1 + index * 2).padStart(2, '0')}`,
  count,
}));

const USAGE_ADOPTION: FeatureAdoption[] = [
  {
    key: 'hasCustomTheme',
    label: 'Custom colours',
    numerator: 62,
    denominator: 100,
    percentage: 62,
  },
  {
    key: 'multiScreen',
    label: 'More than one screen',
    numerator: 48,
    denominator: 100,
    percentage: 48,
  },
  {
    key: 'everPresented',
    label: 'Ever presented',
    numerator: 34,
    denominator: 100,
    percentage: 34,
  },
  { key: 'hasLogo', label: 'Logo uploaded', numerator: 27, denominator: 100, percentage: 27 },
  {
    key: 'teamWorkspace',
    label: 'Team (2+ members)',
    numerator: 21,
    denominator: 100,
    percentage: 21,
  },
  {
    key: 'hasBackgroundImage',
    label: 'Background image',
    numerator: 14,
    denominator: 100,
    percentage: 14,
  },
];

/**
 * Eight weeks ending on a Saturday, so the strip shows a full weekend at each
 * end — the recessed ground is the thing under test and it has to be checked
 * at the edges, where a rect can round off the chart.
 */
const ACTIVITY_DAYS: DailyPoint[] = Array.from({ length: 56 }, (_, index) => {
  const day = new Date(Date.UTC(2026, 5, 28));
  day.setUTCDate(day.getUTCDate() + index);
  const weekday = day.getUTCDay();
  const weekend = weekday === 0 || weekday === 6;
  return {
    day: day.toISOString().slice(0, 10),
    // A shape with a real rhythm rather than noise: quiet weekends, a rising
    // trend, and one dead week in the middle to prove a gap reads as a gap.
    count:
      index >= 21 && index <= 27 ? 0 : weekend ? 2 + (index % 3) : 18 + index * 2 - (index % 5) * 6,
  };
});

/** Derived, never typed in: a fixture that contradicts its own chart teaches
 * the reader to distrust the chart. The real page computes both from one
 * EventsOverview, so the gallery must not be the only place they can disagree. */
const ACTIVITY_TOTAL = ACTIVITY_DAYS.reduce((sum, point) => sum + point.count, 0);
const ACTIVITY_BUSIEST = ACTIVITY_DAYS.reduce((best, point) =>
  point.count > best.count ? point : best,
);

const ACTIVITY_BREAKDOWN: EventBreakdown[] = (
  [
    ['screenAdvanced', 1840],
    ['pollOpened', 402],
    ['resultsViewed', 268],
    ['presentationStarted', 96],
    ['pollEdited', 88],
    ['votingClosed', 61],
    ['resultsExported', 34],
    ['pollCreated', 27],
    ['memberInvited', 9],
    ['folderCreated', 2],
  ] as const
).map(([key, count], _index, entries) => ({
  key,
  label: ADMIN_EVENT_LABELS[key],
  count,
  percentage: Math.round((count / entries.reduce((sum, [, n]) => sum + n, 0)) * 100),
}));

const ACTION_UNIT = { one: 'action', many: 'actions' };
const SIGNUP_UNIT = { one: 'signup', many: 'signups' };

/**
 * A channel mix with a long tail, which is the case the ledger has to survive:
 * a leader far ahead of everything else, and entries small enough that a bar
 * scaled to the total would vanish. Percentages are derived for the same
 * reason ACTIVITY_BREAKDOWN's are.
 */
const SIGNUP_BREAKDOWN: SignupSourceBreakdown[] = (
  [
    ['join', 214],
    ['producthunt', 96],
    ['direct', 71],
    ['kahoot-alternative', 33],
    ['ads-alternative', 12],
    ['conferences', 4],
    ['other', 1],
  ] as const
).map(([key, count], _index, entries) => ({
  key,
  label: SIGNUP_SOURCE_LABELS[key],
  count,
  percentage: Math.round((count / entries.reduce((sum, [, n]) => sum + n, 0)) * 100),
}));

const SIGNUP_TOTAL = SIGNUP_BREAKDOWN.reduce((sum, entry) => sum + entry.count, 0);

const USAGE_WORKSPACES: WorkspaceUsageRow[] = [
  {
    workspaceId: 'ws_1',
    name: 'Acme Offsite',
    members: 12,
    quizzes: 9,
    sessionsStarted: 21,
    votesInSessions: 842,
    createdAt: '2026-05-12T09:00:00.000Z',
    lastActiveAt: '2026-08-07T10:00:00.000Z',
    isLiveNow: true,
    plan: 'marquee',
    subscribed: true,
  },
  {
    workspaceId: 'ws_2',
    name: 'Northwind Product Team',
    members: 4,
    quizzes: 3,
    sessionsStarted: 5,
    votesInSessions: 118,
    createdAt: '2026-06-01T09:00:00.000Z',
    lastActiveAt: '2026-08-02T09:00:00.000Z',
    isLiveNow: false,
    plan: 'granted',
    subscribed: true,
  },
  {
    workspaceId: 'ws_3',
    name: "Tommy's personal workspace",
    members: 1,
    quizzes: 1,
    sessionsStarted: 0,
    votesInSessions: 0,
    createdAt: '2026-08-01T09:00:00.000Z',
    lastActiveAt: null,
    isLiveNow: false,
    plan: 'free',
    subscribed: false,
  },
];

/**
 * The followspot with measured geometry rather than hard-coded offsets.
 *
 * The four panels are placed off the control they surround, which is what the
 * overlay does at runtime — a version with pixel offsets typed in looks right
 * once and then lies the first time the button's padding changes.
 */
function SpotlightDemo() {
  const target = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = target.current;
    if (!element) return;
    const measure = () =>
      setHole({
        top: element.offsetTop - 8,
        left: element.offsetLeft - 8,
        width: element.offsetWidth + 16,
        height: element.offsetHeight + 16,
      });
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div style={{ position: 'relative', padding: '28px 0' }}>
      {/* Centred, so the demo has room on all four sides and actually shows
          four panels rather than two. */}
      <div className="panel inline-form" ref={target} style={{ margin: '0 auto', maxWidth: 420 }}>
        <input aria-label="New poll title" placeholder="Name your next poll…" readOnly />
        <button type="button">Create poll</button>
      </div>
      {hole ? (
        <div className="tour tour-still" style={{ position: 'absolute', inset: 0 }}>
          <div
            className="tour-scrim"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, height: hole.top }}
          />
          <div
            className="tour-scrim"
            style={{
              position: 'absolute',
              top: hole.top + hole.height,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <div
            className="tour-scrim"
            style={{
              position: 'absolute',
              top: hole.top,
              left: 0,
              width: hole.left,
              height: hole.height,
            }}
          />
          <div
            className="tour-scrim"
            style={{
              position: 'absolute',
              top: hole.top,
              left: hole.left + hole.width,
              right: 0,
              height: hole.height,
            }}
          />
          <div className="tour-ring" style={{ position: 'absolute', ...hole }} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The builder, wired to local state instead of the API.
 *
 * Seeded so every state the running order can draw is on screen at once: a
 * finished screen, one with a blank option, and an empty one. Reordering,
 * follow-ups and branding are all live here — this is the only way to see the
 * page without a signed-in session and a saved poll.
 */
function BuilderDemo() {
  const [screens, setScreens] = useState<PollScreen[]>(() => [
    {
      screenId: 'sc_1',
      questions: [
        {
          questionId: 'qn_1',
          text: 'Which release should we ship first?',
          options: [
            { optionId: 'op_1', text: 'Realtime collaboration' },
            { optionId: 'op_2', text: 'Offline mode' },
            {
              optionId: 'op_3',
              text: 'Public API',
              followUp: { message: 'The API beta opens in March.', url: 'https://example.org' },
            },
          ],
        },
        {
          questionId: 'qn_2',
          text: 'How many of these have you tried?',
          multiSelect: true,
          options: [
            { optionId: 'op_4', text: 'None yet' },
            { optionId: 'op_5', text: 'One or two' },
          ],
        },
      ],
    },
    {
      screenId: 'sc_2',
      questions: [
        {
          questionId: 'qn_3',
          text: 'How did the session land?',
          options: [
            { optionId: 'op_6', text: 'Worth the time' },
            { optionId: 'op_7', text: '' },
          ],
        },
      ],
    },
    { screenId: 'sc_3', questions: [] },
  ]);
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME);

  return (
    <div className="builder">
      <div className="builder-body">
        <aside className="builder-rail">
          <RunningOrder screens={screens} />
          <a className="rail-jump" href="#gallery-branding">
            Branding
          </a>
        </aside>
        <div className="builder-canvas">
          <div className="title-field field">
            <label htmlFor="gallery-title">Title</label>
            <input id="gallery-title" defaultValue="Kickoff poll" />
          </div>

          {screens.map((screen, index) => (
            <ScreenEditor
              key={screen.screenId}
              screen={screen}
              quizId="qz_gallery"
              index={index}
              total={screens.length}
              disabled={false}
              onChange={(next) => setScreens(screens.map((s, i) => (i === index ? next : s)))}
              onMove={(direction) => {
                const target = index + direction;
                if (target < 0 || target >= screens.length) return;
                const next = [...screens];
                const [moved] = next.splice(index, 1);
                if (moved) next.splice(target, 0, moved);
                setScreens(next);
              }}
              onRemove={() => setScreens(screens.filter((_, i) => i !== index))}
              canMoveQuestion={(questionIndex, direction) =>
                canMoveQuestion(screens, index, questionIndex, direction)
              }
              onMoveQuestion={(questionIndex, direction) =>
                setScreens(moveQuestion(screens, index, questionIndex, direction))
              }
            />
          ))}

          <button
            type="button"
            className="ghost-add ghost-add-screen"
            onClick={() =>
              setScreens([...screens, { screenId: `sc_${screens.length + 1}`, questions: [] }])
            }
          >
            Add screen
          </button>

          <div id="gallery-branding">
            <ThemeEditor quizId="quiz-1" theme={theme} disabled={false} onChange={setTheme} />
          </div>
        </div>
      </div>

      <div className="sticky-actions">
        <button type="button">Save changes</button>
        <p className="save-state muted">Unsaved changes</p>
        <button type="button" className="secondary quiet-danger">
          Delete poll
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <p className="eyebrow" style={{ marginBottom: 12 }}>
        {title}
      </p>
      {children}
    </section>
  );
}

export function Gallery() {
  const [votes, setVotes] = useState(QUESTIONS);

  const addVote = () =>
    setVotes(([q]) => {
      if (!q) return QUESTIONS;
      const options = q.options.map((o, i) => (i === 0 ? { ...o, count: o.count + 3 } : o));
      const total = options.reduce((s, o) => s + o.count, 0);
      return [
        {
          ...q,
          totalVotes: total,
          options: options.map((o) => ({
            ...o,
            percentage: Math.round((o.count / total) * 1000) / 10,
          })),
        },
      ];
    });

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto' }}>
      <h1>Style gallery</h1>
      <p className="muted">Dev-only. Every state, no backend.</p>

      <Section title="Brand">
        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Logo size={128} />
          <Logo size={64} />
          <Logo size={32} />
          <Logo size={24} />
          {/* Tab size. If the seam stops reading here, the mark is just a
              letter in a box and the concept is gone. */}
          <Logo size={16} />
        </div>
        <div style={{ marginTop: 24 }}>
          <Brand />
        </div>
      </Section>

      <Section title="Code cells">
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <CodeCells code="ABC234" size="sm" />
          <CodeCells code="ABC234" size="md" />
        </div>
      </Section>

      <Section title="Buttons">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="button">Create account</button>
          <button type="button" className="secondary">
            Open big screen
          </button>
          <button type="button" className="danger">
            End presentation
          </button>
          <button type="button" disabled>
            Saving…
          </button>
          <span className="lamp">Live</span>
        </div>
      </Section>

      <Section title="Builder">
        <BuilderDemo />
      </Section>

      <Section title="Quiz card">
        <div className="quiz-grid" style={{ maxWidth: 320 }}>
          {/* The title's link is stretched over the card, which is what lets a
              card that opens a poll also hold its own Duplicate button. */}
          <article className="quiz-card">
            <div className="quiz-card-head">
              <h2>
                <a className="quiz-card-link" href="#cards" onClick={(e) => e.preventDefault()}>
                  Sprint retro
                </a>
              </h2>
              <span className="lamp">Live</span>
            </div>
            <CodeCells code="ABC234" size="sm" />
            <p className="muted">3 screens · 5 questions</p>
            <p className="muted">48 votes collected</p>
            <button type="button" className="secondary quiz-card-duplicate">
              Duplicate
            </button>
          </article>
        </div>
      </Section>

      <Section title="Console results (animated — press the button)">
        <div className="panel" style={{ marginTop: 0 }}>
          <ResultBars questions={votes} animated />
        </div>
        <button type="button" className="secondary" style={{ marginTop: 12 }} onClick={addVote}>
          Simulate 3 votes
        </button>
      </Section>

      <Section title="Presenter deck">
        <div className="panel presenter-controls" style={{ marginTop: 0 }}>
          <button type="button" className="secondary">
            ← Back
          </button>
          <div className="cue-dots" aria-hidden="true">
            <span className="cue" />
            <span className="cue active" />
            <span className="cue" />
            <span className="cue" />
          </div>
          <span className="presenter-position">2 / 4</span>
          <button type="button">Next →</button>
          <span className="live-dot">Live</span>
        </div>
      </Section>

      <Section title="Big screen (on stage)">
        <div
          className="big-screen"
          style={{ minHeight: 520, borderRadius: 18, overflow: 'hidden' }}
        >
          <header className="big-screen-head">
            <div>
              <div className="big-screen-title-row">
                <h1>Sprint retro</h1>
                <span className="lamp">Live</span>
              </div>
            </div>
            <div className="join-panel">
              <div className="join-details">
                <p className="join-url">floorpoll.app/j/ABC234</p>
                <CodeCells code="ABC234" size="lg" flip />
              </div>
            </div>
          </header>
          <main className="big-screen-body cols-1 rows-1">
            <div className="screen-swap">
              <ResultBars questions={votes} large animated />
            </div>
          </main>
          {/* #91 — what a free workspace's room sees. Shown here unconditionally
              because this page is where the mark is reviewed; on the real
              surface it rides on the server's `vendorMark` and a paid plan
              never receives a true. */}
          <VendorMark />
        </div>
      </Section>

      <Section title="Taker (phone, on stage)">
        <div
          className="taker"
          style={{ minHeight: 480, maxWidth: 390, borderRadius: 18, overflow: 'hidden' }}
        >
          <section className="taker-question">
            <h2>Which release should we ship first?</h2>
            <p className="answer-recorded" role="status">
              Answer recorded
            </p>
            <div className="taker-options locked">
              <button type="button" className="taker-option chosen" disabled>
                Realtime collaboration
                <span className="paddle-check" aria-hidden="true">
                  ✓
                </span>
              </button>
              <button type="button" className="taker-option" disabled>
                Offline mode
              </button>
              <button type="button" className="taker-option" disabled>
                Public API
              </button>
            </div>
          </section>
          <p className="taker-footer">Results are on the big screen.</p>
          <VendorMark />
        </div>
      </Section>

      {/* The two states the mode adds. A raised paddle wears the ring; only
          handing it in earns the ✓, so the check keeps meaning "counted". */}
      <Section title="Taker — several answers, mid-pick">
        <div
          className="taker"
          style={{ minHeight: 420, maxWidth: 390, borderRadius: 18, overflow: 'hidden' }}
        >
          <section className="taker-question">
            <h2>Which releases should we ship this quarter?</h2>
            <p className="taker-rule">Pick as many as you like, then hand them in.</p>
            <div className="taker-options">
              <button type="button" className="taker-option chosen">
                Realtime collaboration
                <span className="paddle-check paddle-raised" aria-hidden="true" />
              </button>
              <button type="button" className="taker-option">
                Offline mode
              </button>
              <button type="button" className="taker-option chosen">
                Public API
                <span className="paddle-check paddle-raised" aria-hidden="true" />
              </button>
            </div>
            <button type="button" className="taker-submit block">
              Submit 2 picks
            </button>
          </section>
        </div>
      </Section>

      <Section title="Taker — several answers, handed in">
        <div
          className="taker"
          style={{ minHeight: 420, maxWidth: 390, borderRadius: 18, overflow: 'hidden' }}
        >
          <section className="taker-question">
            <h2>Which releases should we ship this quarter?</h2>
            <p className="answer-recorded" role="status">
              Votes recorded
            </p>
            <div className="followup-pick">
              <p className="followup-for">Realtime collaboration</p>
              <div className="followup">
                <p className="followup-message">Shipping in the March release.</p>
              </div>
            </div>
            <div className="followup-pick">
              <p className="followup-for">Public API</p>
              <div className="followup">
                <p className="followup-message">The beta is open now.</p>
              </div>
            </div>
            <div className="taker-options locked">
              <button type="button" className="taker-option chosen" disabled>
                Realtime collaboration
                <span className="paddle-check" aria-hidden="true">
                  ✓
                </span>
              </button>
              <button type="button" className="taker-option" disabled>
                Offline mode
              </button>
              <button type="button" className="taker-option chosen" disabled>
                Public API
                <span className="paddle-check" aria-hidden="true">
                  ✓
                </span>
              </button>
            </div>
          </section>
        </div>
      </Section>

      <Section title="Big screen — several answers (percentages sum past 100)">
        <div
          className="big-screen"
          style={{ minHeight: 360, borderRadius: 18, overflow: 'hidden' }}
        >
          <main className="big-screen-body cols-1 rows-1">
            <div className="screen-swap">
              <ResultBars questions={[multiVotes]} large />
            </div>
          </main>
        </div>
      </Section>

      <Section title="Big screen — a rating, drawn as columns">
        <div
          className="big-screen"
          style={{ minHeight: 420, borderRadius: 18, overflow: 'hidden' }}
        >
          <main className="big-screen-body cols-1 rows-1">
            <div className="screen-swap">
              <ResultBars questions={[npsVotes]} large />
            </div>
          </main>
        </div>
      </Section>

      <Section title="Usage — operator dashboard">
        <div className="usage-kpis">
          <StatTile label="Users" value={129} delta={41} deltaPeriod="90d" />
          <StatTile label="Workspaces" value={96} delta={28} deltaPeriod="90d" />
          <StatTile label="Polls" value={214} delta={77} deltaPeriod="90d" />
          <StatTile label="Sessions run" value={88} delta={52} deltaPeriod="90d" />
          <StatTile label="Votes in sessions" value={5140} delta={3006} deltaPeriod="90d" />
          <StatTile label="Polls (falling)" value={214} delta={-12} deltaPeriod="90d" />
          <StatTile label="Polls (flat)" value={214} delta={0} deltaPeriod="90d" />
        </div>
        <div className="usage-charts">
          <UsageColumns
            points={USAGE_WEEKS}
            title="Signups per week"
            caption="New accounts, last 12 weeks"
            unit="signups"
          />
          <UsageColumns
            points={USAGE_WEEKS.map((p) => ({ ...p, count: 0 }))}
            title="Empty series"
            caption="Every week zero — the axis must still render"
            unit="sessions"
          />
        </div>
        <div className="panel">
          <h2 className="panel-title">Feature adoption</h2>
          <p className="panel-caption">Sorted most to least adopted</p>
          <AdoptionBars adoption={USAGE_ADOPTION} />
        </div>
        <div className="panel">
          <h2 className="panel-title">Feature adoption — empty</h2>
          <p className="panel-caption">No adoption facts yet</p>
          <AdoptionBars adoption={[]} />
        </div>
        <div className="panel activity-panel">
          <div className="activity-head">
            <div>
              <h2 className="panel-title">Console activity</h2>
              <p className="panel-caption">
                What signed-in people did, counted across every workspace. Anonymous voters are not
                included.
              </p>
            </div>
            <div className="page-head-actions" role="group" aria-label="Daily range">
              <button type="button" className="secondary">
                14d
              </button>
              <button type="button" className="secondary">
                28d
              </button>
              <button type="button" aria-pressed="true">
                56d
              </button>
            </div>
          </div>
          <p className="activity-summary">
            <strong>{ACTIVITY_TOTAL.toLocaleString()}</strong> actions ·{' '}
            <strong>{Math.round(ACTIVITY_TOTAL / ACTIVITY_DAYS.length)}</strong> a day on average ·
            busiest <strong>{ACTIVITY_BUSIEST.count}</strong> on {ACTIVITY_BUSIEST.day}
          </p>
          <EventDayStrip
            points={ACTIVITY_DAYS}
            label="Console actions per day"
            unit={ACTION_UNIT}
          />
          <h3 className="activity-subtitle">What they clicked</h3>
          <Ledger rows={ACTIVITY_BREAKDOWN} unit={ACTION_UNIT} />
        </div>

        <div className="panel activity-panel">
          <h2 className="panel-title">Console activity — nothing counted</h2>
          <p className="panel-caption">The axis and the empty ledger must both still render</p>
          <EventDayStrip
            points={ACTIVITY_DAYS.map((point) => ({ ...point, count: 0 }))}
            label="Console actions per day"
            unit={ACTION_UNIT}
          />
          <h3 className="activity-subtitle">What they clicked</h3>
          <Ledger rows={[]} unit={ACTION_UNIT} />
        </div>

        {/* The same two components carrying the other series, which is the
            point of sampling it: a change that reads well for actions and
            badly for signups shows up here rather than on the live page. */}
        <div className="panel activity-panel">
          <h2 className="panel-title">Where signups come from</h2>
          <p className="panel-caption">New accounts by the channel their signup link claimed</p>
          <p className="activity-summary">
            <strong>{SIGNUP_TOTAL.toLocaleString()}</strong> signups · led by{' '}
            <strong>{SIGNUP_BREAKDOWN[0]?.label}</strong> at {SIGNUP_BREAKDOWN[0]?.percentage}%
          </p>
          <EventDayStrip points={ACTIVITY_DAYS} label="Signups per day" unit={SIGNUP_UNIT} />
          <h3 className="activity-subtitle">Which channel</h3>
          <Ledger rows={SIGNUP_BREAKDOWN} unit={SIGNUP_UNIT} />
        </div>

        <WorkspaceUsageTable workspaces={USAGE_WORKSPACES} />
        <WorkspaceUsageTable workspaces={[]} />
      </Section>

      {/* The guide's two surfaces. Both are position: fixed in the app, so each
          is unpinned here to sit in the flow — the styling under test is the
          card and the dock, not where the overlay puts them. */}
      <Section title="Guide — step card">
        <div style={{ position: 'relative', height: 250 }}>
          <div className="tour" style={{ position: 'absolute', inset: 0 }}>
            <div
              className="tour-card"
              style={{ position: 'absolute', top: 0, left: 0, width: 336 }}
            >
              <div className="tour-card-head">
                <span className="tour-counter" aria-hidden="true">
                  <span className="code-cell tour-counter-cell">0</span>
                  <span className="code-cell tour-counter-cell">7</span>
                </span>
                <p className="eyebrow">of 11</p>
              </div>
              <h2>Three questions, ten options</h2>
              <p>
                A screen holds one to three questions so it stays readable from the back of the
                room. Each question takes two to ten options.
              </p>
              <div className="tour-card-actions">
                <button type="button" className="tour-skip">
                  Leave guide
                </button>
                <div className="tour-card-steps">
                  <button type="button" className="secondary">
                    Back
                  </button>
                  <button type="button">Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Guide — last step and paused dock">
        <div style={{ position: 'relative', height: 260 }}>
          <div className="tour" style={{ position: 'absolute', inset: 0 }}>
            <div
              className="tour-card"
              style={{ position: 'absolute', top: 0, left: 0, width: 336 }}
            >
              <div className="tour-card-head">
                <span className="tour-counter" aria-hidden="true">
                  <span className="code-cell tour-counter-cell">1</span>
                  <span className="code-cell tour-counter-cell">1</span>
                </span>
                <p className="eyebrow">of 11</p>
              </div>
              <h2>After the show</h2>
              <p>
                Votes accumulate across presentations, so a poll can run again next week and keep
                counting. Reset zeroes the counts and lets a phone vote a second time.
              </p>
              <p className="tour-card-note">
                Replay this guide any time from the foot of the page.
              </p>
              <div className="tour-card-actions">
                <button type="button" className="tour-skip">
                  Leave guide
                </button>
                <div className="tour-card-steps">
                  <button type="button" className="secondary">
                    Back
                  </button>
                  <button type="button">Finish</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Guide — paused dock">
        {/* Unpinned into the flow. In the app it is fixed to the bottom-left of
            the viewport, so faking a corner inside a small box here would only
            measure the box. */}
        <div className="tour-dock" style={{ position: 'relative', left: 0, bottom: 0 }}>
          <div>
            <p className="eyebrow">Guide paused</p>
            <p className="tour-dock-line">Open a poll and the guide picks up in the builder.</p>
          </div>
          <button type="button" className="secondary">
            Leave guide
          </button>
        </div>
      </Section>

      <Section title="Guide — followspot on a control">
        <SpotlightDemo />
      </Section>

      {/* Every plan state at once. `PlanView` is the pure half of the account
          page's card precisely so this can render it without a session or a
          Stripe account behind it. */}
      <Section title="Account — plan states">
        {/* Both sides of the vendor mark's switch-on (#91), on fixed clocks:
            what a free workspace is told is coming, and what it is told once
            the mark is live. */}
        <PlanView
          view={{ pro: false, configured: true }}
          workspaceName="Acme all-hands"
          at={new Date('2026-09-01T00:00:00.000Z')}
        />
        <PlanView
          view={{ pro: false, configured: true }}
          workspaceName="Acme all-hands"
          at={new Date('2026-11-01T00:00:00.000Z')}
        />
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: '2027-09-22T00:00:00.000Z',
              source: 'subscription',
              subscriptionStatus: 'active',
              cancelAtPeriodEnd: false,
            },
          }}
          workspaceName="Acme all-hands"
        />
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: '2027-09-22T00:00:00.000Z',
              source: 'subscription',
              subscriptionStatus: 'active',
              cancelAtPeriodEnd: true,
            },
          }}
          workspaceName="Acme all-hands"
        />
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: { proUntil: '2027-09-22T00:00:00.000Z', source: 'event_pass' },
          }}
          workspaceName="Rigging Co. showcase"
        />
        {/* Entitled with no `source` — not reachable through the normal write
            path, since services/billing.ts derives `source` from `proUntil` on
            every save; this is what a restore or hand edit looks like. Reads
            as Marquee rather than Free, which is what `mark` below has always
            drawn it as. */}
        <PlanView
          view={{ pro: true, configured: true, billing: { proUntil: '2027-09-22T00:00:00.000Z' } }}
          workspaceName="Union Hall Trivia"
        />
        {/* Cancelled but not yet run out: still Marquee, and nothing is sold
            on top of it. */}
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: '2027-09-22T00:00:00.000Z',
              renewsAt: '2027-09-22T00:00:00.000Z',
              source: 'subscription',
              subscriptionStatus: 'canceled',
              cancelAtPeriodEnd: true,
            },
          }}
          workspaceName="Acme all-hands"
        />
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: '2027-09-22T00:00:00.000Z',
              source: 'subscription',
              subscriptionStatus: 'past_due',
            },
          }}
          workspaceName="Acme all-hands"
        />
        {/* Comped by an operator. Reads as a plan, not a freebie, and is
            offered nothing. */}
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: { proUntil: GRANT_FOREVER, source: 'grant' },
          }}
          workspaceName="Conference partner"
        />
        {/* Comped on top of a live subscription: still shows the real renewal. */}
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: GRANT_FOREVER,
              renewsAt: '2027-09-22T00:00:00.000Z',
              source: 'grant',
              subscriptionStatus: 'active',
              cancelAtPeriodEnd: false,
            },
          }}
          workspaceName="Acme all-hands"
        />
        {/* Comped while a card problem gets sorted out. The likeliest reason to
            grant at all, and the one place the copy must not blame the card. */}
        <PlanView
          view={{
            pro: true,
            configured: true,
            billing: {
              proUntil: GRANT_FOREVER,
              renewsAt: '2027-09-22T00:00:00.000Z',
              source: 'grant',
              subscriptionStatus: 'past_due',
            },
          }}
          workspaceName="Northwind"
        />
      </Section>
    </div>
  );
}
